import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import * as ambientLife from "../../util/ambientLife";
import { greetingPartForHour, sanitizeAmbient } from "../../util/ambientSettings";
import { capitalizeFirstLetter } from "../../util/helper";

/*
 * "Good afternoon, Chris" in the HEADER, as the first item of the nav list, with a thin line between it and
 * Playback.
 *
 * WHERE IT LIVES, AND WHY IT MOVED. It first sat in a 22px strip between the header and the grey panel. Measured
 * on a real screenshot: 5px under the header, 4px above the panel, beside the blue drawer button. Chris: "the spacing
 * up here looks more crowded." The panel's own top band is the stage, so it cannot go there either ("that should
 * only be the area where bubbles should be"). The header's right half is empty, so it is the nav's first item: it
 * is in normal flow, so it needs no position measuring, and the nav stays put because the list grows LEFTWARDS
 * (it is pinned to the header's right edge).
 *
 * IT MUST NEVER COLLIDE WITH THE SITE NAME. The site name is centred and its width is the customer's own hostname,
 * so the room between it and Playback is anything from 500px to none. The component measures that room and either
 * truncates the line to fit or, below a readable minimum, leaves it out. It does not guess from the window width.
 *
 * It is shown on the same terms as the rest of ambient life (ambientLife.ts): only while the site is quiet AND the
 * dashboard is connected, fading with everything else. The name is the account's first name; with none it says only
 * the greeting, with no comma.
 */
const GAP = 28;          // clear space kept between the site name and the line
const PIPE = 15;         // the greeting's own right padding plus its 1px divider
const MIN_TEXT = 110;    // below this much room the line is left out instead of truncated to nothing

const AmbientGreeting: FC = () => {
  const { t } = useTranslation();
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const config = sanitizeAmbient(authReducer?.bubbleClusterParameters);
  const ref = useRef<HTMLLIElement>(null);
  const [visible, setVisible] = useState<boolean>(ambientLife.isActive());
  const [hour, setHour] = useState<number>(() => new Date().getHours());
  const [room, setRoom] = useState<number | null>(null);

  useEffect(() => ambientLife.subscribe(setVisible), []);

  // the words change at 5:00, 12:00, 17:00 and 22:00, so re-read the hour once a minute
  useEffect(() => {
    const id = window.setInterval(() => setHour(new Date().getHours()), 60000);
    return () => window.clearInterval(id);
  }, []);

  // how much room is there between the site name and Playback?
  useLayoutEffect(() => {
    const li = ref.current;
    const header = li?.closest(".header") as HTMLElement | null;
    if (!li || !header) return;
    const measure = () => {
      const site = header.querySelector(".header-site") as HTMLElement | null;
      const playback = li.nextElementSibling as HTMLElement | null;
      if (!site || !playback) return;
      setRoom(Math.floor(playback.getBoundingClientRect().left - site.getBoundingClientRect().right - GAP - PIPE));
    };
    measure();
    const late = window.setTimeout(measure, 400);   // the site name may still be loading
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(header);
    const site = header.querySelector(".header-site");
    if (site) ro?.observe(site);
    return () => {
      window.clearTimeout(late);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [config.ambientGreeting]);

  if (!config.ambientGreeting) return null;

  const first = String(authReducer?.authData?.firstname ?? "").trim();
  const name = first ? String(capitalizeFirstLetter(first)) : "";
  const words = t(`greeting_${greetingPartForHour(hour)}`);
  const cramped = room !== null && room < MIN_TEXT;

  return (
    <li
      ref={ref}
      className={`ambient-greeting-item${visible && !cramped ? " is-visible" : ""}${cramped ? " is-cramped" : ""}`}
      aria-hidden={!visible || cramped}
    >
      <span className="ambient-greeting" style={room !== null && !cramped ? { maxWidth: room } : undefined}>
        {words}
        {name ? (
          <>
            , <b>{name}</b>
          </>
        ) : null}
      </span>
    </li>
  );
};

export default AmbientGreeting;
